import React, { useEffect, useMemo, useState } from 'react';
import { ThemeContext } from '../../../module/ThemeManager';
import { Box, Button, Grid, Typography } from '@mui/material';
import { theme } from '../../../constants/theme';
import { ISearchGridProps, SearchGrid } from '../../EsCarousels/SearchGrid';
import { DiscoverySearchUnparsedResponse, ISearchUgcCampaign } from '../../../types/global/generic.types';
import { IWatchGrid, WatchGrid } from '../../EsCarousels/WatchGrid';
import { ActCarousel, IActCarousel } from '../../EsCarousels/ActCarousel';
import { IUserCarousel, UserCarousel } from '../../EsCarousels/UserCarousel';
import { IContest, IProject, UserSummary } from '../../../types/global/helper';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { isEmpty } from '../../../utils/lodash';
import { VideoThumbnail } from '../../VideoPlayer/VideoThumbnail';
import { UserCard } from '../../EsCards/NewUserCard';
import { IProjectSummary } from '../../../types/global/media.types';
import { useSelector } from 'react-redux';
import { CategorySuggestionShimmer } from '../../../pages/CategorySuggestionShimmer';
import { currentFilterCategorySelector, loadingDiscoverySelector } from '../../../selectors/selectors';
import { MainCategoryShimmer } from '../../../pages/MainCategoryShimmer/MainCategoryShimmer';
import { RootState } from '../../../redux/root-reducer';
import { EsFollowButton } from '../../EsButtons/EsFollowButton';
import { alpha } from '@mui/system';
import { useThemeContext } from '../../../hook/useThemeManager';
import { UGCCampaignCard } from '../../EsCards/UGCCampaignCard';
import type { IUGCCampaign } from '../../EsCards/UGCCampaignCard';

interface ISearchContainerProps extends IWatchGrid, IActCarousel, IUserCarousel, ISearchGridProps {
    data: DiscoverySearchUnparsedResponse;
    suggestedContest?: IContest[];
    suggestedTalent?: UserSummary[];
    suggestedProjects?: IProject[];
    handleProjectSuggestionClick?: (project: IProjectSummary) => void;
    handleContestSuggestionCLick?: (contest: IContest) => void;
    handleUserSuggestionClick?: (user: UserSummary) => void;
}

const SearchContainer: React.FC<ISearchContainerProps> = props => {
    const {
        data,
        suggestedContest,
        suggestedProjects,
        suggestedTalent,
        onActPressed,
        onClickActUserIcon,
        handleProjectSuggestionClick,
        handleContestSuggestionCLick,
        handleUserSuggestionClick,
    } = props;
    const { themeStyle } = useThemeContext();
    const isLoading = useSelector(loadingDiscoverySelector);
    const selectedFilterCategory = useSelector(currentFilterCategorySelector);
    const searchTerm = useSelector((state: RootState) => state.discovery.searchTerm || '');
    const [showAllSuggestions, setShowAllSuggestions] = useState(false);
    const searchResultCount = useMemo(() => {
        if (!data) return 0;
        const projectsCount = Array.isArray(data.projects) ? data.projects.length : 0;
        const actsCount = Array.isArray(data.acts) ? data.acts.length : 0;
        const usersCount = Array.isArray(data.users) ? data.users.length : 0;
        const contestsCount = Array.isArray(data.contests) ? data.contests.length : 0;
        const campaignsCount = Array.isArray(data.campaigns) ? data.campaigns.length : 0;
        return projectsCount + actsCount + usersCount + contestsCount + campaignsCount;
    }, [data]);
    const suggestedTitle = useMemo(() => {
        if (isLoading) {
            if (selectedFilterCategory === 'contest') return 'Campaigns';
            if (selectedFilterCategory === 'campaigns') return 'Brand Campaigns';
            if (selectedFilterCategory === 'talent') return 'People';
            if (selectedFilterCategory === 'video') return 'UGC';
            return '';
        }
        if (!data) return '';
        if (!isEmpty(data.projects)) return 'UGC';
        if (!isEmpty(data.acts)) return 'Acts';
        if (!isEmpty(data.users)) return 'People';
        if (!isEmpty(data.contests)) return 'Campaigns';
        if (!isEmpty(data.campaigns)) return 'Brand Campaigns';
        return '';
    }, [data, isLoading, selectedFilterCategory]);
    const searchSummaryText = useMemo(() => {
        const normalized = suggestedTitle.toLowerCase();
        if (normalized === 'campaigns') {
            return 'Here are contests related to your search. Explore and find what interests you.';
        }
        if (normalized === 'brand campaigns') {
            return 'Discover paid and gifted brand campaigns you can join as a creator.';
        }
        if (normalized === 'people') {
            return 'Browse matching profiles and discover talent.';
        }
        if (normalized === 'ugc' || normalized === 'acts') {
            return 'Discover matching UGC and creative work based on your search.';
        }
        return 'Browse matching results related to your search.';
    }, [suggestedTitle]);
    const suggestionType = useMemo(() => {
        const normalized = suggestedTitle.toLowerCase();
        if (normalized === 'people') return 'users';
        if (normalized === 'campaigns') return 'contests';
        if (normalized === 'brand campaigns') return 'brandCampaigns';
        if (normalized === 'ugc' || normalized === 'acts') return 'projects';
        return normalized;
    }, [suggestedTitle]);
    const shouldShowSectionTitles = suggestionType === 'projects';
    const shouldShowResultPrefix = useMemo(() => {
        return suggestionType === 'users';
    }, [suggestionType]);
    const resultPrefixText = useMemo(() => {
        if (!shouldShowResultPrefix || searchResultCount <= 0) return '';
        const trimmedSearchTerm = searchTerm.trim();
        if (trimmedSearchTerm) {
            return `${searchResultCount} results for "${trimmedSearchTerm}". `;
        }
        return `${searchResultCount} results found. `;
    }, [shouldShowResultPrefix, searchResultCount, searchTerm]);
    const effectiveSuggestedProjects = useMemo(() => suggestedProjects || [], [suggestedProjects]);
    const suggestedPeople = useMemo(() => suggestedTalent || [], [suggestedTalent]);
    const visibleSuggestedPeople = useMemo(
        () => (showAllSuggestions ? suggestedPeople : suggestedPeople.slice(0, 3)),
        [showAllSuggestions, suggestedPeople],
    );
    const hasSuggestions = useMemo(() => {
        switch (suggestionType) {
            case 'users':
                return suggestedPeople.length > 0;

            case 'projects':
                return !isEmpty(effectiveSuggestedProjects);

            case 'contests':
                return Array.isArray(suggestedContest) && suggestedContest.length > 0;

            case 'brandCampaigns':
                return Array.isArray(data.campaigns) && data.campaigns.length > 0;

            default:
                return false; // ✅ safer fallback
        }
    }, [suggestionType, suggestedPeople, effectiveSuggestedProjects, suggestedContest, data.campaigns]);

    useEffect(() => {
        setShowAllSuggestions(false);
    }, [suggestionType, suggestedPeople.length]);

    const renderSuggestion = () => {
        const getClosingLabel = (expirationDate?: string) => {
            if (!expirationDate) return '';
            const now = new Date();
            const end = new Date(expirationDate);
            const diffMs = end.getTime() - now.getTime();
            const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
            if (days < 0) return 'Ended';
            if (days === 0) return 'Ends today';
            if (days === 1) return 'Ends in 1 day';
            return `Ends in ${days} days`;
        };

        switch (suggestionType) {
            case 'projects':
                return (
                    !isEmpty(effectiveSuggestedProjects) &&
                    effectiveSuggestedProjects.map((proj, index) => (
                        <VideoThumbnail
                            project={proj}
                            onClick={() =>
                                handleProjectSuggestionClick && handleProjectSuggestionClick(proj as IProjectSummary)
                            }
                            key={index.toString()}
                            isPlayingIconEnabled={false}
                            size="small"
                        />
                    ))
                );
            case 'brandCampaigns':
                return (
                    !isEmpty(data.campaigns) && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {(showAllSuggestions ? data.campaigns : data.campaigns.slice(0, 3)).map(
                                (c: ISearchUgcCampaign) => (
                                    <UGCCampaignCard key={c._id} campaign={c as unknown as IUGCCampaign} />
                                ),
                            )}
                        </Box>
                    )
                );
            case 'contests':
                return (
                    suggestedContest &&
                    !isEmpty(suggestedContest) && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                            {suggestedContest.map((contest, index) => (
                                <Box
                                    key={index.toString()}
                                    onClick={() =>
                                        handleContestSuggestionCLick && handleContestSuggestionCLick(contest)
                                    }
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        p: 1.5,
                                        borderRadius: 2,
                                        backgroundColor: theme[themeStyle].deep_grey_lighter,
                                        transition: 'background-color 0.2s ease',
                                        cursor: 'pointer',
                                        flexShrink: 0,
                                        '&:hover': {
                                            backgroundColor: theme[themeStyle].deep_grey,
                                        },
                                        '&:hover .suggested-campaign-image': {
                                            transform: 'scale(1.08)',
                                        },
                                    }}>
                                    <Box
                                        sx={{
                                            width: 80,
                                            height: 80,
                                            borderRadius: 1.5,
                                            overflow: 'hidden',
                                            flexShrink: 0,
                                        }}>
                                        <Box
                                            component="img"
                                            src={contest.feature_image}
                                            alt={contest.title}
                                            className="suggested-campaign-image"
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                transition: 'transform 0.5s ease',
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                color: theme[themeStyle].tag_white,
                                                fontSize: 14,
                                                lineHeight: 1.15,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}>
                                            {contest.title}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: 'rgba(255,255,255,0.66)',
                                                fontSize: 12,
                                                mt: 0.55,
                                                display: '-webkit-box',
                                                overflow: 'hidden',
                                                WebkitBoxOrient: 'vertical',
                                                WebkitLineClamp: 2,
                                                lineHeight: 1.3,
                                            }}>
                                            {(contest.description || '')
                                                .replace(/<[^>]*>/g, ' ')
                                                .replace(/\s+/g, ' ')
                                                .trim()}
                                        </Typography>
                                        {!!getClosingLabel(contest.expiration_date) && (
                                            <Typography
                                                sx={{
                                                    mt: 0.85,
                                                    color: theme[themeStyle].highlight,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    fontSize: 11,
                                                }}>
                                                {getClosingLabel(contest.expiration_date)}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )
                );
            case 'users':
                return (
                    !isEmpty(suggestedPeople) && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 3,
                                width: '100%',
                            }}>
                            {suggestedPeople.map((user, index) => (
                                <Box
                                    key={index.toString()}
                                    sx={{
                                        width: '100%',
                                        overflow: 'hidden',
                                        borderRadius: 2,
                                        flexShrink: 0,
                                    }}>
                                    <UserCard
                                        user={user}
                                        onClickUserCard={() =>
                                            handleUserSuggestionClick && handleUserSuggestionClick(user)
                                        }
                                    />
                                </Box>
                            ))}
                        </Box>
                    )
                );
            default:
                return (
                    !isEmpty(effectiveSuggestedProjects) &&
                    effectiveSuggestedProjects.map((proj, index) => (
                        <VideoThumbnail
                            project={proj}
                            onClick={() =>
                                handleProjectSuggestionClick && handleProjectSuggestionClick(proj as IProjectSummary)
                            }
                            key={index.toString()}
                            isPlayingIconEnabled={false}
                            size="small"
                        />
                    ))
                );
        }
    };

    return (
        <ThemeContext.Consumer>
            {({ device }) => {
                return (
                    <Grid
                        container
                        style={{ height: '100%' }}
                        columnSpacing={{ xs: 0, md: 4, lg: 5 }}
                        rowSpacing={{ xs: 3, md: 0 }}
                        sx={{
                            paddingLeft: { xs: '16px', sm: '30px', lg: '40px' },
                            paddingRight: { xs: '16px', sm: '30px', lg: '40px' },
                            paddingTop: { xs: '0px', sm: '28px', lg: '36px' },
                            paddingBottom: { xs: '24px', sm: '28px', lg: '36px' },
                            marginBottom: { xs: '0px', sm: '80px' },
                            minHeight: '100dvh',
                            width: '100%',
                            maxWidth: '100%',
                            minWidth: 0,
                            boxSizing: 'border-box',
                            alignItems: 'flex-start',
                        }}>
                        <Grid item xs={12} md={12} lg={hasSuggestions ? 8 : 12} sx={{ minWidth: 0 }}>
                            <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: { xs: '2rem', sm: '2.5rem', lg: '3rem' },
                                        lineHeight: 1,
                                        letterSpacing: '-0.03em',
                                        color: theme[themeStyle].tag_white,
                                        textTransform: 'capitalize',
                                    }}>
                                    {suggestedTitle}
                                </Typography>
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        mb: { xs: 0.35, sm: 0.5 },
                                        backgroundColor: theme[themeStyle].true_highlight,
                                    }}
                                />
                            </Box>
                            <Typography
                                sx={{
                                    mb: 3,
                                    color: 'rgba(255,255,255,0.76)',
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: { xs: '0.92rem', sm: '1rem' },
                                    lineHeight: 1.45,
                                }}>
                                {resultPrefixText}
                                {searchSummaryText}
                            </Typography>
                            {!!data && !isLoading ? (
                                <>
                                    {!isEmpty(data.projects) && (
                                        <Box sx={{ mb: { xs: 4, md: 5 } }}>
                                            <WatchGrid
                                                categoryTitle={shouldShowSectionTitles ? 'UGC' : ''}
                                                mediaData={data.projects}
                                                forceTwoColumns={hasSuggestions && suggestionType === 'projects'}
                                                {...props}
                                            />
                                        </Box>
                                    )}
                                    {!isEmpty(data.acts) && (
                                        <Box sx={{ mb: { xs: 4, md: 5 } }}>
                                            <ActCarousel
                                                actsData={data.acts as IProject[]}
                                                title={shouldShowSectionTitles ? 'Acts' : ''}
                                                onActPressed={onActPressed}
                                                onClickActUserIcon={onClickActUserIcon}
                                                {...props}
                                            />
                                        </Box>
                                    )}
                                    {!isEmpty(data.users) && (
                                        <Box sx={{ mb: { xs: 4, md: 5 } }}>
                                            <UserCarousel
                                                userData={data.users}
                                                title={shouldShowSectionTitles ? 'People' : ''}
                                                forceTwoColumns={hasSuggestions && suggestionType === 'users'}
                                                {...props}
                                            />
                                        </Box>
                                    )}
                                    {!isEmpty(data.contests) && (
                                        <Box sx={{ mb: { xs: 4, md: 5 } }}>
                                            <SearchGrid
                                                searchResult={data.contests}
                                                title={shouldShowSectionTitles ? 'Campaigns' : ''}
                                                forceTwoColumns={hasSuggestions && suggestionType === 'contests'}
                                                {...props}
                                            />
                                        </Box>
                                    )}
                                    {!isEmpty(data.campaigns) && (
                                        <Box sx={{ mb: { xs: 4, md: 5 } }}>
                                            {(shouldShowSectionTitles || selectedFilterCategory === 'campaigns') && (
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                                        lineHeight: 1.2,
                                                        color: theme[themeStyle].filters_white,
                                                        mb: 2,
                                                    }}>
                                                    Brand Campaigns
                                                </Typography>
                                            )}
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gap: { xs: 2.5, md: 3 },
                                                    gridTemplateColumns: {
                                                        xs: '1fr',
                                                        sm: 'repeat(2, minmax(0, 1fr))',
                                                        md: 'repeat(2, minmax(0, 1fr))',
                                                        lg:
                                                            hasSuggestions &&
                                                            (suggestionType === 'brandCampaigns' ||
                                                                suggestionType === 'contests')
                                                                ? 'repeat(2, minmax(0, 1fr))'
                                                                : 'repeat(3, minmax(0, 1fr))',
                                                        xl:
                                                            hasSuggestions &&
                                                            (suggestionType === 'brandCampaigns' ||
                                                                suggestionType === 'contests')
                                                                ? 'repeat(2, minmax(0, 1fr))'
                                                                : 'repeat(3, minmax(0, 1fr))',
                                                    },
                                                    width: '100%',
                                                }}>
                                                {data.campaigns.map((c: ISearchUgcCampaign) => (
                                                    <UGCCampaignCard
                                                        key={c._id}
                                                        campaign={c as unknown as IUGCCampaign}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </>
                            ) : (
                                <MainCategoryShimmer />
                            )}
                        </Grid>
                        {!device.isMobile && isLoading ? (
                            <CategorySuggestionShimmer />
                        ) : !device.isMobile && !isLoading && hasSuggestions ? (
                            <Grid
                                item
                                xs={12}
                                md={12}
                                lg={4}
                                sx={{ display: { xs: 'none', md: 'block' }, minWidth: 0, maxWidth: '100%' }}>
                                {suggestionType === 'users' ? (
                                    <Box
                                        sx={{
                                            backgroundColor: alpha(theme[themeStyle].true_black, 0.5),
                                            borderRadius: 4,
                                            overflow: 'hidden',
                                            position: { xs: 'static', lg: 'sticky' },
                                            top: { lg: 88 },
                                            border: '1px solid rgba(255,255,255,0.06)',
                                            boxShadow: '0 20px 40px rgba(0,0,0,0.22)',
                                            p: 3,
                                        }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                mb: 3,
                                            }}>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    color: theme[themeStyle].tag_white,
                                                    fontSize: 18,
                                                    lineHeight: 1.05,
                                                }}>
                                                Suggested People
                                            </Typography>
                                            <Box
                                                sx={{
                                                    color: theme[themeStyle].highlight,
                                                    fontSize: 10,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.13em',
                                                    px: 1.2,
                                                    py: 0.6,
                                                    borderRadius: 1,
                                                    bgcolor: 'rgba(255,159,77,0.12)',
                                                }}>
                                                Discover
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.2 }}>
                                            {visibleSuggestedPeople.map((user, index) => {
                                                const fullName = `${user.firstName || ''} ${
                                                    user.lastName || ''
                                                }`.trim();
                                                const displayName = fullName || user.userName || 'Unknown User';
                                                const primaryRole = Array.isArray(user.user_type)
                                                    ? user.user_type[0]
                                                    : '';
                                                const locationAddress = user.location?.address || '';
                                                const subtitle = [primaryRole, locationAddress]
                                                    .filter(Boolean)
                                                    .join(' • ');
                                                return (
                                                    <Box
                                                        key={`${user._id || user.userName || index}`}
                                                        onClick={() =>
                                                            handleUserSuggestionClick && handleUserSuggestionClick(user)
                                                        }
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1.6,
                                                            cursor: 'pointer',
                                                        }}>
                                                        <Box
                                                            sx={{
                                                                width: 56,
                                                                height: 56,
                                                                borderRadius: '50%',
                                                                overflow: 'hidden',
                                                                flexShrink: 0,
                                                                border: '2px solid transparent',
                                                                transition: 'border-color 0.2s ease',
                                                                '&:hover': {
                                                                    borderColor: theme[themeStyle].highlight,
                                                                },
                                                            }}>
                                                            <Box
                                                                component="img"
                                                                src={user.profile_picture}
                                                                alt={displayName}
                                                                sx={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover',
                                                                }}
                                                            />
                                                        </Box>
                                                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                            <Typography
                                                                sx={{
                                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                                    color: theme[themeStyle].tag_white,
                                                                    fontSize: 17,
                                                                    lineHeight: 1.15,
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                }}>
                                                                {displayName}
                                                            </Typography>
                                                            {!!subtitle && (
                                                                <Typography
                                                                    sx={{
                                                                        color: 'rgba(255,255,255,0.62)',
                                                                        fontSize: 12,
                                                                        mt: 0.4,
                                                                        whiteSpace: 'nowrap',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                    }}>
                                                                    {subtitle}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <EsFollowButton
                                                            intrinsic
                                                            location="suggestion"
                                                            user={user}
                                                            isFollowing={
                                                                Array.isArray((user as any)?.user_follow_interaction) &&
                                                                (user as any).user_follow_interaction.length > 0
                                                            }
                                                            buttonSx={{
                                                                width: 'auto',
                                                                minWidth: 0,
                                                                height: 30,
                                                                px: 1.2,
                                                                py: 1,
                                                                borderRadius: 999,
                                                                border: '1px solid rgba(255,255,255,0.25)',
                                                                color: theme[themeStyle].tag_white,
                                                                backgroundColor: 'transparent',
                                                                fontSize: 11,
                                                                lineHeight: 1,
                                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                                textTransform: 'none',
                                                                '&:hover': {
                                                                    border: '1px solid rgba(255,255,255,0.4)',
                                                                    backgroundColor: 'rgba(255,255,255,0.05)',
                                                                    color: theme[themeStyle].tag_white,
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                );
                                            })}
                                        </Box>

                                        {suggestedPeople.length > 4 && !showAllSuggestions && (
                                            <Button
                                                fullWidth
                                                onClick={() => setShowAllSuggestions(true)}
                                                sx={{
                                                    mt: 3,
                                                    py: 1.5,
                                                    borderRadius: 999,
                                                    bgcolor: 'rgba(255,255,255,0.06)',
                                                    border: '1px solid rgba(255,255,255,0.08)',
                                                    color: theme[themeStyle].highlight,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    textTransform: 'none',
                                                    fontSize: 14,
                                                    '&:hover': {
                                                        bgcolor: 'rgba(255,255,255,0.1)',
                                                    },
                                                }}>
                                                View All Suggestions
                                            </Button>
                                        )}
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            backgroundColor: theme[themeStyle].deep_grey,
                                            borderRadius: 3,
                                            overflowX: 'hidden',
                                            position:
                                                suggestionType === 'contests' ||
                                                suggestionType === 'projects' ||
                                                suggestionType === 'brandCampaigns'
                                                    ? { xs: 'static', lg: 'sticky' }
                                                    : 'sticky',
                                            top: suggestionType === 'contests' ? { lg: 88 } : 88,
                                            border: '1px solid rgba(255,255,255,0.06)',
                                            boxShadow: '0 20px 40px rgba(0,0,0,0.22)',
                                        }}>
                                        <Box
                                            sx={{
                                                backgroundColor: 'transparent',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                px: 3,
                                                py: 2.5,
                                                borderBottom: '1px solid rgba(255,255,255,0.06)',
                                            }}>
                                            <Typography
                                                style={{
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    color: theme[themeStyle].tag_white,
                                                    fontSize: 20,
                                                    textTransform: 'capitalize',
                                                }}>
                                                Suggested {suggestedTitle}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                p: 2,
                                                overflowY: 'auto',
                                            }}>
                                            {renderSuggestion()}
                                        </Box>
                                    </Box>
                                )}
                            </Grid>
                        ) : null}
                    </Grid>
                );
            }}
        </ThemeContext.Consumer>
    );
};

export { SearchContainer };
